import { ChangeDetectorRef, Component, inject, Input, input, output, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DxcSelectModule, ThemeModule } from '@dxc-technology/halstack-angular';
import { EmittedValue, SelectProperties } from '@dxc-technology/halstack-angular/lib/dxc-select/dxc-select.types';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipCustomDirective } from '@shared/directives/tooltip-custom.directive';
import { RequiredControlValueAccessor } from '@shared/interface/common.interface';

@Component({
  selector: 'custom-select',
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.scss'],
  imports: [ThemeModule, DxcSelectModule, TranslateModule, TooltipCustomDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CustomSelectComponent,
      multi: true,
    },
  ],
})
export class CustomSelectComponent implements RequiredControlValueAccessor {
  readonly label = input<string>(undefined);
  readonly keyError = input<string>(undefined);

  /**Text to be put as placeholder of the select. */
  readonly placeholder = input<string>('label.common.chooseAnOption');

  /**Size of the margin to be applied to the component ('xxsmall' | 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge'). You can pass an object with 'top', 'bottom', 'left' and 'right' properties in order to specify different margin sizes. */
  readonly margin = input<SelectProperties['margin']>(undefined);

  /**Value of the select. If undefined, the component will be uncontrolled and the value will be managed internally by the component. */
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() value: any;
  readonly model = input<any>(undefined);
  readonly required = input<boolean>(false);

  /**If true, the select component will support multiple selected options. In that case, value will be an array of strings with each selected option value. */
  readonly multiple = input<boolean>(false);

  /**List of options. The following interfaces should be imported.
   *
   * Option:
   * - Label: string: Label of the option to be shown in the select's listbox.
   * - Value: string: Value of the option. It should be unique and not an empty string, which is reserved to the empty option added by optional prop.
   * - Icon: string: Element used as the icon that will be placed before the option label.
   *
   * OptionGroup:
   * - Label: string: Label of the group to be shown in the select's listbox.
   * - Options: Option[]: List of the grouped options. */
  readonly options = input<SelectProperties['options']>(undefined);

  /**Size of the component ('small' | 'medium' | 'large' | 'fillParent'). */
  readonly size = input<SelectProperties['size']>(undefined);

  /**If it is a defined value and also a truthy string, the component will change its appearance, showing the error below the select component. If the defined value is an empty string, it will reserve a space below the component for a future error, but it would not change its look. In case of being undefined or null, both the appearance and the space for the error message would not be modified. */
  readonly errorMessage = input<string>(undefined);

  /**If true, the component will be disabled. */
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() disabled: boolean = false;

  /**If true, enables search functionality. */
  readonly searchable = input<boolean>(true);

  /**This event will be emitted when the select loses the focus. An object including the value (or values) and the error (if the value selected is not valid) will be passed to this function. An example of this object is: { value: value, error: error }. If there is no error, error will be null. */
  readonly blur = output<EmittedValue>();

  /**This event will be emitted when the user selects an option. An object including the new value (or values) and the error (if the value selected is not valid) will be passed to this function. An example of this object is: { value: value, error: error}. If there is no error, error will be null. */
  readonly valueChange = output<EmittedValue>();

  protected _errorMessage: string = '';

  cdr = inject(ChangeDetectorRef);
  translate = inject(TranslateService);

  onChange(event: EmittedValue) {
    this.valueChange.emit(event);
    this.onChangeForm?.(event.value);
    this.writeValue?.(event.value);
  }

  onBlur(event: EmittedValue) {
    this.blur.emit(event);
    this.onTouched?.();
  }

  ngAfterViewChecked(): void {
    this.setTranslateErrorMessage(this.errorMessage());
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setTranslateErrorMessage(changes?.['errorMessage']?.currentValue);
  }

  setTranslateErrorMessage(message: string): void {
    if (message?.includes('[not_blank]')) {
      let localizeLabel = this.translate.instant(this.keyError() || this.label());
      this._errorMessage = localizeLabel + this.translate.instant('label.common.cannotBlank');
      return;
    }
    this._errorMessage = message;
  }

  onChangeForm: (value: any) => void;
  onTouched: () => void;

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChangeForm = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getSelectedLabel(value: string): string {
    if (!value) return '';
    const opts = this.options?.() || this.options?.call?.(this) || []; // supports signal/getter cases
    for (const o of opts as any[]) {
      if (o && 'value' in o && o.value === value) return o.label;
      if (o && 'options' in o) {
        const found = (o.options || []).find((x: any) => x.value === value);
        if (found) return found.label;
      }
    }
    return '';
  }
}
